/**
 * React wrapper for Bryntum Gantt
 */
import React from 'react';
import { AlignSpec, AssignmentModel, AssignmentModelConfig, Base, Baselines, BaselinesConfig, CalendarModel, CalendarModelConfig, CellCopyPaste, CellCopyPasteConfig, CellEdit, CellEditConfig, CellMenu, CellMenuConfig, CellTooltip, CellTooltipConfig, ColumnAutoWidth, ColumnAutoWidthConfig, ColumnDragToolbar, ColumnDragToolbarConfig, ColumnLines, ColumnLinesConfig, ColumnPicker, ColumnPickerConfig, ColumnRename, ColumnRenameConfig, ColumnReorder, ColumnReorderConfig, ColumnResize, ColumnResizeConfig, ColumnStore, ColumnStoreConfig, ContainerItemConfig, ContainerLayoutConfig, CriticalPaths, CriticalPathsConfig, Dependencies, DependenciesConfig, DependencyEdit, DependencyEditConfig, DependencyModel, DependencyModelConfig, EventFilter, EventFilterConfig, EventSegments, EventSegmentsConfig, FillHandle, FillHandleConfig, Filter, FilterBar, FilterBarConfig, FilterConfig, GanttFeaturesConfigType, GridColumnConfig, GridExcelExporter, GridExcelExporterConfig, GridGroupSummary, GridGroupSummaryConfig, Group, GroupConfig, HeaderMenu, HeaderMenuConfig, HeaderZoom, HeaderZoomConfig, Indicators, IndicatorsConfig, Labels, LabelsConfig, Layout, Location, Mask, MaskConfig, MergeCells, MergeCellsConfig, Model, ModelConfig, MspExport, MspExportConfig, MultiPageExporter, MultiPageExporterConfig, MultiPageVerticalExporter, MultiPageVerticalExporterConfig, NonWorkingTime, NonWorkingTimeConfig, Pan, PanConfig, PanelCollapser, PanelHeader, ParentArea, ParentAreaConfig, PdfExport, PdfExportConfig, PercentBar, PercentBarConfig, PresetStore, ProgressLine, ProgressLineConfig, ProjectLines, ProjectLinesConfig, ProjectModel, ProjectModelConfig, QuickFind, QuickFindConfig, Rectangle, RegionResize, RegionResizeConfig, ResourceModel, ResourceModelConfig, Rollups, RollupsConfig, RowCopyPaste, RowCopyPasteConfig, RowExpander, RowExpanderConfig, RowReorder, RowReorderConfig, ScheduleMenu, ScheduleMenuConfig, ScheduleTooltip, ScheduleTooltipConfig, ScrollManager, ScrollManagerConfig, ScrollOptions, Scroller, ScrollerConfig, Search, SearchConfig, SinglePageExporter, SinglePageExporterConfig, Sort, SortConfig, StateProvider, StickyCells, StickyCellsConfig, Store, StoreConfig, Stripe, StripeConfig, SubGridConfig, Summary, SummaryConfig, TabConfig, TaskCopyPaste, TaskCopyPasteConfig, TaskDrag, TaskDragConfig, TaskDragCreate, TaskDragCreateConfig, TaskEdit, TaskEditConfig, TaskMenu, TaskMenuConfig, TaskModel, TaskModelConfig, TaskNonWorkingTime, TaskNonWorkingTimeConfig, TaskResize, TaskResizeConfig, TaskSegmentDrag, TaskSegmentDragConfig, TaskSegmentResize, TaskSegmentResizeConfig, TaskStore, TaskTooltip, TaskTooltipConfig, TimeAxis, TimeAxisColumnConfig, TimeAxisConfig, TimeAxisHeaderMenu, TimeAxisHeaderMenuConfig, TimeRanges, TimeRangesConfig, TimeSpan, TimeSpanConfig, TimelineBase, Tool, ToolConfig, ToolbarConfig, TooltipConfig, Tree, TreeConfig, TreeGroup, TreeGroupConfig, Versions, VersionsConfig, ViewPreset, ViewPresetConfig, Widget } from '@bryntum/gantt';
import { Gantt } from '@bryntum/gantt';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumGanttProps =  {
     // Configs only
    adopt?                                 : HTMLElement|string
    align?                                 : AlignSpec|string
    allowCreate?                           : boolean
    allowOver?                             : boolean
    anchor?                                : boolean
    anchorToTarget?                        : boolean
    animateRemovingRows?                   : boolean
    ariaDescription?                       : string
    ariaLabel?                             : string
    autoAdjustTimeAxis?                    : boolean
    autoClose?                             : boolean
    autoHeight?                            : boolean
    autoShow?                              : boolean
    autoUpdateRecord?                      : boolean
    bbar?                                  : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    bodyCls?                               : string|object
    bubbleEvents?                          : object
    bufferCoef?                            : number
    bufferThreshold?                       : number
    callOnFunctions?                       : boolean
    centered?                              : boolean
    closable?                              : boolean
    closeAction?                           : 'hide'|'destroy'
    collapsed?                             : boolean
    collapsible?                           : boolean|PanelCollapser
    columns?                               : ColumnStore|Partial<GridColumnConfig>[]|Partial<ColumnStoreConfig>
    config?                                : object
    constrainTo?                           : HTMLElement|Widget|Rectangle
    contentElementCls?                     : string|object
    contextMenuTriggerEvent?               : 'contextmenu'|'click'|'dblclick'
    creationTooltip?                       : Partial<TooltipConfig>
    cycleResolutionPopupClass?             : Function
    defaultBindProperty?                   : string
    defaultFocus?                          : Function
    defaultRegion?                         : string
    defaultResourceImageName?              : string
    defaults?                              : object
    dependencyIdField?                     : string
    destroyStore?                          : boolean
    disableGridRowModelWarning?            : boolean
    dismissDelay?                          : number
    displaySchedulingIssueResolutionPopup? : boolean
    dock?                                  : 'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|object
    draggable?                             : boolean|object
    durationDisplayPrecision?              : number|boolean
    enableDeleteKey?                       : boolean
    enableRecurringEvents?                 : boolean
    enableSticky?                          : boolean
    enableTextSelection?                   : boolean
    eventStyle?                            : 'plain'|'border'|'colored'|'hollow'|'line'|'dashed'|'minimal'|'rounded'|'calendar'|'interday'|null
    features?                              : object
    fillLastColumn?                        : boolean
    fixedRowHeight?                        : boolean
    floating?                              : boolean
    focusOnToFront?                        : boolean
    footer?                                : object|string
    forElement?                            : HTMLElement
    forSelector?                           : string
    fullRowRefresh?                        : boolean
    getDateConstraints?                    : Function
    getHtml?                               : Function|string
    getRowHeight?                          : Function
    header?                                : string|boolean|PanelHeader
    hideAnimation?                         : boolean|object
    hideDelay?                             : number
    hideHeaders?                           : boolean
    hideOnDelegateChange?                  : boolean
    hideWhenEmpty?                         : boolean
    hoverDelay?                            : number
    htmlCls?                               : string|object
    ignoreParentReadOnly?                  : boolean
    infiniteScroll?                        : boolean
    itemCls?                               : string
    keyMap?                                : Record<string, string>
    lazyItems?                             : Record<string, Partial<ContainerItemConfig>>|Partial<ContainerItemConfig>[]|Widget[]
    listeners?                             : object
    loadingMsg?                            : string
    loadMask?                              : string|Partial<MaskConfig>|null
    loadMaskDefaults?                      : Partial<MaskConfig>|Mask
    loadMaskError?                         : Partial<MaskConfig>|Mask|boolean
    localeClass?                           : typeof Base
    localizable?                           : boolean
    localizableProperties?                 : string[]
    managedEventSizing?                    : boolean
    maskDefaults?                          : Partial<MaskConfig>
    masked?                                : boolean|string|Partial<MaskConfig>
    maximizable?                           : boolean
    maximized?                             : boolean
    maxTimeAxisUnit?                       : 'millisecond'|'second'|'minute'|'hour'|'day'|'week'|'month'|'quarter'|'year'
    modal?                                 : boolean
    monitorResize?                         : boolean
    mouseOffsetX?                          : number
    mouseOffsetY?                          : number
    namedItems?                            : Record<string, Partial<ContainerItemConfig>>
    newTaskDefaults?                       : object|Function
    owner?                                 : Widget
    partner?                               : TimelineBase
    plugins?                               : Function[]
    positioned?                            : boolean
    preserveFocusOnDatasetChange?          : boolean
    preserveScrollOnDatasetChange?         : boolean
    preventTooltipOnTouch?                 : boolean
    projectProgressReporting?              : string|null
    relayStoreEvents?                      : boolean
    resizeToFitIncludesHeader?             : boolean
    resourceImageFolderPath?               : string
    responsiveLevels?                      : Record<string, number|string>
    ripple?                                : boolean|object
    rootElement?                           : ShadowRoot
    schedulingIssueResolutionPopupClass?   : Function
    scrollAction?                          : 'hide'|'realign'|null
    scrollerClass?                         : typeof Scroller
    scrollManager?                         : Partial<ScrollManagerConfig>|ScrollManager
    scrollTaskIntoViewOnCellClick?         : boolean|ScrollOptions
    selectionMode?                         : object
    showAnimation?                         : boolean|object
    showCreationTooltip?                   : boolean
    showDirty?                             : boolean
    showOnClick?                           : boolean
    showOnHover?                           : boolean
    showTooltip?                           : boolean
    showTooltipWhenDisabled?               : boolean
    snapRelativeToEventStartDate?          : boolean
    stateful?                              : boolean|object|string[]
    statefulEvents?                        : object|string[]
    stateId?                               : string
    stateProvider?                         : StateProvider
    stickyHeaders?                         : boolean
    strips?                                : Record<string, Partial<ContainerItemConfig>>
    subGridConfigs?                        : Record<string, Partial<SubGridConfig>>
    suppressFit?                           : boolean
    syncMask?                              : string|Partial<MaskConfig>|null
    tab?                                   : boolean|Partial<TabConfig>
    tag?                                   : string
    taskRenderer?                          : Function
    tbar?                                  : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    terminalCls?                           : string
    terminalSides?                         : string[]
    textAlign?                             : 'left'|'center'|'right'|'start'|'end'
    textContent?                           : boolean
    timeAxis?                              : Partial<TimeAxisConfig>|TimeAxis
    timeAxisColumn?                        : Partial<TimeAxisColumnConfig>
    trackMouse?                            : boolean
    trapFocus?                             : boolean
    ui?                                    : string|object
    visibleZoomFactor?                     : number
    weekStartDay?                          : number
    weight?                                : number
    zoomKeepsOriginalTimespan?             : boolean
    zoomOnMouseWheel?                      : boolean
    zoomOnTimeAxisDoubleClick?             : boolean

     // Configs and properties
    alignSelf?                     : string
    allowDropOnEventBar?           : boolean
    appendTo?                      : HTMLElement|string
    assignments?                   : AssignmentModel[]|Partial<AssignmentModelConfig>[]
    barMargin?                     : number
    calendars?                     : CalendarModel[]|Partial<CalendarModelConfig>[]
    cls?                           : string|object
    columnLines?                   : boolean
    content?                       : string
    creationTooltipTemplate?       : Function
    data?                          : object[]|Model[]|Partial<ModelConfig>[]
    dataset?                       : object
    dependencies?                  : DependencyModel[]|Partial<DependencyModelConfig>[]
    disabled?                      : boolean
    displayDateFormat?             : string
    emptyText?                     : string
    enableEventAnimations?         : boolean
    enableUndoRedoKeys?            : boolean
    endDate?                       : Date|string
    eventColor?                    : 'red'|'pink'|'purple'|'violet'|'indigo'|'blue'|'cyan'|'teal'|'green'|'lime'|'yellow'|'orange'|'deep-orange'|'gray'|'gantt-green'|null
    extraData?                     : string|number|boolean|object
    fillTicks?                     : boolean
    flex?                          : number|string
    forceFit?                      : boolean
    height?                        : number|string
    hidden?                        : boolean
    html?                          : string|Function
    id?                            : string
    insertBefore?                  : HTMLElement|string
    insertFirst?                   : HTMLElement|string
    items?                         : Widget[]|Partial<ContainerItemConfig>[]|Record<string, Partial<ContainerItemConfig>>|Record<string, Partial<ContainerItemConfig>|boolean|null>
    layout?                        : Layout|string|Partial<ContainerLayoutConfig>
    layoutStyle?                   : object
    longPressTime?                 : number
    margin?                        : number|string
    maxHeight?                     : string|number
    maxWidth?                      : string|number
    maxZoomLevel?                  : number
    minHeight?                     : string|number
    minWidth?                      : string|number
    minZoomLevel?                  : number
    presets?                       : PresetStore|Partial<ViewPresetConfig>[]
    project?                       : ProjectModel|Partial<ProjectModelConfig>
    readOnly?                      : boolean
    record?                        : Model
    resources?                     : ResourceModel[]|Partial<ResourceModelConfig>[]
    rowHeight?                     : number
    scrollable?                    : Scroller|boolean|Partial<ScrollerConfig>
    snap?                          : boolean
    startDate?                     : Date|string
    store?                         : Store|Partial<StoreConfig>
    strictRecordMapping?           : boolean
    tasks?                         : TaskModel[]|Partial<TaskModelConfig>[]
    taskStore?                     : TaskStore
    tickSize?                      : number
    timeRanges?                    : TimeSpan[]|Partial<TimeSpanConfig>[]
    timeZone?                      : string|number
    title?                         : string
    toggleParentTasksOnClick?      : boolean
    tools?                         : Record<string, Tool|Partial<ToolConfig>>|Record<string, Tool>|Record<string, Partial<ToolConfig>>
    tooltip?                       : string|Partial<TooltipConfig>
    tooltipTemplate?               : Function
    transitionDuration?            : number
    updateTimelineContextOnScroll? : boolean
    viewPreset?                    : ViewPreset|string|Partial<ViewPresetConfig>
    visibleDate?                   : object|Date
    width?                         : number|string
    workingTime?                   : object
    x?                             : number
    y?                             : number

     // Properties only
    anchorSize?      : number[]
    eventColors?     : string[]
    eventStyles?     : string[]
    isSettingValues? : boolean
    isValid?         : boolean
    scrollLeft?      : number
    scrollTop?       : number
    scrollX?         : number
    selectedCell?    : Location
    selectedCells?   : Location[]
    selectedRecord?  : Model
    selectedRecords? : Model[]|number[]
    selectedRows?    : Model[]|number[]
    state?           : object
    timeResolution?  : object|number
    type?            : string
    values?          : Record<string, object>
    zoomLevel?       : number

     // Events
    onAfterDependencySave?              : Function
    onAfterDragCreate?                  : Function
    onAfterEventSave?                   : Function
    onAfterTaskDrop?                    : Function
    onAfterTaskEdit?                    : Function
    onAfterTaskSave?                    : Function
    onAfterTaskSegmentDrop?             : Function
    onBeforeAssignmentDelete?           : Function
    onBeforeCancelCellEdit?             : Function
    onBeforeCellDelete?                 : Function
    onBeforeCellEditStart?              : Function
    onBeforeClose?                      : Function
    onBeforeColumnDragStart?            : Function
    onBeforeColumnDropFinalize?         : Function
    onBeforeCopy?                       : Function
    onBeforeDependencyAdd?              : Function
    onBeforeDependencyDelete?           : Function
    onBeforeDependencyEdit?             : Function
    onBeforeDependencyEditShow?         : Function
    onBeforeDependencySave?             : Function
    onBeforeDestroy?                    : Function
    onBeforeDragCreate?                 : Function
    onBeforeDragCreateFinalize?         : Function
    onBeforeEventDelete?                : Function
    onBeforeEventResize?                : Function
    onBeforeEventResizeFinalize?        : Function
    onBeforeEventSave?                  : Function
    onBeforeEventSegmentResize?         : Function
    onBeforeEventSegmentResizeFinalize? : Function
    onBeforeFinishCellEdit?             : Function
    onBeforeHide?                       : Function
    onBeforeMspExport?                  : Function
    onBeforePan?                        : Function
    onBeforePaste?                      : Function
    onBeforePdfExport?                  : Function
    onBeforePresetChange?               : Function
    onBeforeRenderRow?                  : Function
    onBeforeRenderRows?                 : Function
    onBeforeSelectionChange?            : Function
    onBeforeSetRecord?                  : Function
    onBeforeShow?                       : Function
    onBeforeTaskAdd?                    : Function
    onBeforeTaskDelete?                 : Function
    onBeforeTaskDrag?                   : Function
    onBeforeTaskDropFinalize?           : Function
    onBeforeTaskEdit?                   : Function
    onBeforeTaskEditShow?               : Function
    onBeforeTaskResize?                 : Function
    onBeforeTaskResizeFinalize?         : Function
    onBeforeTaskSave?                   : Function
    onBeforeTaskSegmentDrag?            : Function
    onBeforeTaskSegmentDropFinalize?    : Function
    onBeforeTaskSegmentResize?          : Function
    onBeforeTaskSegmentResizeFinalize?  : Function
    onBeforeToggleGroup?                : Function
    onBeforeToggleNode?                 : Function
    onCancelCellEdit?                   : Function
    onCatchAll?                         : Function
    onCellClick?                        : Function
    onCellContextMenu?                  : Function
    onCellDblClick?                     : Function
    onCellMenuBeforeShow?               : Function
    onCellMenuItem?                     : Function
    onCellMenuShow?                     : Function
    onCellMenuToggleItem?               : Function
    onCellMouseOut?                     : Function
    onCellMouseOver?                    : Function
    onCollapseNode?                     : Function
    onColumnDragStart?                  : Function
    onColumnDrop?                       : Function
    onContextMenuItem?                  : Function
    onContextMenuToggleItem?            : Function
    onCopy?                             : Function
    onDataChange?                       : Function
    onDateRangeChange?                  : Function
    onDependenciesDrawn?                : Function
    onDependencyClick?                  : Function
    onDependencyDblClick?               : Function
    onDependencyMouseOut?               : Function
    onDependencyMouseOver?              : Function
    onDestroy?                          : Function
    onDragCreateEnd?                    : Function
    onDragCreateStart?                  : Function
    onDragSelecting?                    : Function
    onEventMenuBeforeShow?              : Function
    onEventMenuItem?                    : Function
    onEventMenuShow?                    : Function
    onEventPartialResize?               : Function
    onEventResizeEnd?                   : Function
    onEventResizeStart?                 : Function
    onEventSegmentPartialResize?        : Function
    onEventSegmentResizeEnd?            : Function
    onEventSegmentResizeStart?          : Function
    onExpandNode?                       : Function
    onFinishCellEdit?                   : Function
    onFocusIn?                          : Function
    onFocusOut?                         : Function
    onGridRowAbort?                     : Function
    onGridRowBeforeDragStart?           : Function
    onGridRowBeforeDropFinalize?        : Function
    onGridRowDrag?                      : Function
    onGridRowDragStart?                 : Function
    onGridRowDrop?                      : Function
    onHeaderMenuBeforeShow?             : Function
    onHeaderMenuItem?                   : Function
    onHeaderMenuShow?                   : Function
    onHeaderMenuToggleItem?             : Function
    onHide?                             : Function
    onMouseOut?                         : Function
    onMouseOver?                        : Function
    onMspExport?                        : Function
    onNavigate?                         : Function
    onPaint?                            : Function
    onPaste?                            : Function
    onPdfExport?                        : Function
    onPercentBarDrag?                   : Function
    onPercentBarDragAbort?              : Function
    onPercentBarDragStart?              : Function
    onPercentBarDrop?                   : Function
    onPointerOver?                      : Function
    onPresetChange?                     : Function
    onReadOnly?                         : Function
    onRecompose?                        : Function
    onReleaseTask?                      : Function
    onRenderRow?                        : Function
    onRenderRows?                       : Function
    onRenderTask?                       : Function
    onResize?                           : Function
    onResponsive?                       : Function
    onScheduleMenuBeforeShow?           : Function
    onScheduleMenuItem?                 : Function
    onScheduleMenuShow?                 : Function
    onScroll?                           : Function
    onSelectionChange?                  : Function
    onSelectionModeChange?              : Function
    onShow?                             : Function
    onStartCellEdit?                    : Function
    onSubGridCollapse?                  : Function
    onSubGridExpand?                    : Function
    onTaskClick?                        : Function
    onTaskContextMenu?                  : Function
    onTaskDblClick?                     : Function
    onTaskDrag?                         : Function
    onTaskDragStart?                    : Function
    onTaskDrop?                         : Function
    onTaskEditCanceled?                 : Function
    onTaskKeyDown?                      : Function
    onTaskKeyUp?                        : Function
    onTaskMenuBeforeShow?               : Function
    onTaskMenuItem?                     : Function
    onTaskMenuShow?                     : Function
    onTaskMouseDown?                    : Function
    onTaskMouseOut?                     : Function
    onTaskMouseOver?                    : Function
    onTaskMouseUp?                      : Function
    onTaskNonWorkingTimeClick?          : Function
    onTaskNonWorkingTimeContextMenu?    : Function
    onTaskNonWorkingTimeDblClick?       : Function
    onTaskPartialResize?                : Function
    onTaskResizeEnd?                    : Function
    onTaskResizeStart?                  : Function
    onTaskSegmentDrag?                  : Function
    onTaskSegmentDragStart?             : Function
    onTaskSegmentDrop?                  : Function
    onTaskSegmentPartialResize?         : Function
    onTaskSegmentResizeEnd?             : Function
    onTaskSegmentResizeStart?           : Function
    onTimeAxisChange?                   : Function
    onTimeAxisHeaderClick?              : Function
    onTimeAxisHeaderContextMenu?        : Function
    onTimeAxisHeaderDblClick?           : Function
    onTimeAxisHeaderMenuBeforeShow?     : Function
    onTimeAxisHeaderMenuItem?           : Function
    onTimeAxisHeaderMenuShow?           : Function
    onTimelineContextChange?            : Function
    onTimelineViewportResize?           : Function
    onTimeRangeHeaderClick?             : Function
    onTimeRangeHeaderContextMenu?       : Function
    onTimeRangeHeaderDblClick?          : Function
    onToggleGroup?                      : Function
    onToggleNode?                       : Function
    onToolClick?                        : Function
    onTransactionChange?                : Function
    onVisibleDateRangeChange?           : Function

     // Features
    baselinesFeature?          : object|boolean|string|Baselines|Partial<BaselinesConfig>
    cellCopyPasteFeature?      : object|boolean|string|CellCopyPaste|Partial<CellCopyPasteConfig>
    cellEditFeature?           : object|boolean|string|CellEdit|Partial<CellEditConfig>
    cellMenuFeature?           : object|boolean|string|CellMenu|Partial<CellMenuConfig>
    cellTooltipFeature?        : object|boolean|string|CellTooltip|Partial<CellTooltipConfig>
    columnAutoWidthFeature?    : object|boolean|string|ColumnAutoWidth|Partial<ColumnAutoWidthConfig>
    columnDragToolbarFeature?  : object|boolean|string|ColumnDragToolbar|Partial<ColumnDragToolbarConfig>
    columnLinesFeature?        : object|boolean|string|ColumnLines|Partial<ColumnLinesConfig>
    columnPickerFeature?       : object|boolean|string|ColumnPicker|Partial<ColumnPickerConfig>
    columnRenameFeature?       : object|boolean|string|ColumnRename|Partial<ColumnRenameConfig>
    columnReorderFeature?      : object|boolean|string|ColumnReorder|Partial<ColumnReorderConfig>
    columnResizeFeature?       : object|boolean|string|ColumnResize|Partial<ColumnResizeConfig>
    criticalPathsFeature?      : object|boolean|string|CriticalPaths|Partial<CriticalPathsConfig>
    dependenciesFeature?       : object|boolean|string|Dependencies|Partial<DependenciesConfig>
    dependencyEditFeature?     : object|boolean|string|DependencyEdit|Partial<DependencyEditConfig>
    eventFilterFeature?        : object|boolean|string|EventFilter|Partial<EventFilterConfig>
    eventSegmentsFeature?      : object|boolean|string|EventSegments|Partial<EventSegmentsConfig>
    excelExporterFeature?      : object|boolean|string|GridExcelExporter|Partial<GridExcelExporterConfig>
    fillHandleFeature?         : object|boolean|string|FillHandle|Partial<FillHandleConfig>
    filterFeature?             : object|boolean|string|Filter|Partial<FilterConfig>
    filterBarFeature?          : object|boolean|string|FilterBar|Partial<FilterBarConfig>
    groupFeature?              : object|boolean|string|Group|Partial<GroupConfig>
    groupSummaryFeature?       : object|boolean|string|GridGroupSummary|Partial<GridGroupSummaryConfig>
    headerMenuFeature?         : object|boolean|string|HeaderMenu|Partial<HeaderMenuConfig>
    headerZoomFeature?         : object|boolean|string|HeaderZoom|Partial<HeaderZoomConfig>
    indicatorsFeature?         : object|boolean|string|Indicators|Partial<IndicatorsConfig>
    labelsFeature?             : object|boolean|string|Labels|Partial<LabelsConfig>
    mergeCellsFeature?         : object|boolean|string|MergeCells|Partial<MergeCellsConfig>
    mspExportFeature?          : object|boolean|string|MspExport|Partial<MspExportConfig>
    multipageFeature?          : object|boolean|string|MultiPageExporter|Partial<MultiPageExporterConfig>
    multipageverticalFeature?  : object|boolean|string|MultiPageVerticalExporter|Partial<MultiPageVerticalExporterConfig>
    nonWorkingTimeFeature?     : object|boolean|string|NonWorkingTime|Partial<NonWorkingTimeConfig>
    panFeature?                : object|boolean|string|Pan|Partial<PanConfig>
    parentAreaFeature?         : object|boolean|string|ParentArea|Partial<ParentAreaConfig>
    pdfExportFeature?          : object|boolean|string|PdfExport|Partial<PdfExportConfig>
    percentBarFeature?         : object|boolean|string|PercentBar|Partial<PercentBarConfig>
    progressLineFeature?       : object|boolean|string|ProgressLine|Partial<ProgressLineConfig>
    projectLinesFeature?       : object|boolean|string|ProjectLines|Partial<ProjectLinesConfig>
    quickFindFeature?          : object|boolean|string|QuickFind|Partial<QuickFindConfig>
    regionResizeFeature?       : object|boolean|string|RegionResize|Partial<RegionResizeConfig>
    rollupsFeature?            : object|boolean|string|Rollups|Partial<RollupsConfig>
    rowCopyPasteFeature?       : object|boolean|string|RowCopyPaste|Partial<RowCopyPasteConfig>
    rowExpanderFeature?        : object|boolean|string|RowExpander|Partial<RowExpanderConfig>
    rowReorderFeature?         : object|boolean|string|RowReorder|Partial<RowReorderConfig>
    scheduleMenuFeature?       : object|boolean|string|ScheduleMenu|Partial<ScheduleMenuConfig>
    scheduleTooltipFeature?    : object|boolean|string|ScheduleTooltip|Partial<ScheduleTooltipConfig>
    searchFeature?             : object|boolean|string|Search|Partial<SearchConfig>
    singlepageFeature?         : object|boolean|string|SinglePageExporter|Partial<SinglePageExporterConfig>
    sortFeature?               : object|boolean|string|Sort|Partial<SortConfig>
    stickyCellsFeature?        : object|boolean|string|StickyCells|Partial<StickyCellsConfig>
    stripeFeature?             : object|boolean|string|Stripe|Partial<StripeConfig>
    summaryFeature?            : object|boolean|string|Summary|Partial<SummaryConfig>
    taskCopyPasteFeature?      : object|boolean|string|TaskCopyPaste|Partial<TaskCopyPasteConfig>
    taskDragFeature?           : object|boolean|string|TaskDrag|Partial<TaskDragConfig>
    taskDragCreateFeature?     : object|boolean|string|TaskDragCreate|Partial<TaskDragCreateConfig>
    taskEditFeature?           : object|boolean|string|TaskEdit|Partial<TaskEditConfig>
    taskMenuFeature?           : object|boolean|string|TaskMenu|Partial<TaskMenuConfig>
    taskNonWorkingTimeFeature? : object|boolean|string|TaskNonWorkingTime|Partial<TaskNonWorkingTimeConfig>
    taskResizeFeature?         : object|boolean|string|TaskResize|Partial<TaskResizeConfig>
    taskSegmentDragFeature?    : object|boolean|string|TaskSegmentDrag|Partial<TaskSegmentDragConfig>
    taskSegmentResizeFeature?  : object|boolean|string|TaskSegmentResize|Partial<TaskSegmentResizeConfig>
    taskTooltipFeature?        : object|boolean|string|TaskTooltip|Partial<TaskTooltipConfig>
    timeAxisHeaderMenuFeature? : object|boolean|string|TimeAxisHeaderMenu|Partial<TimeAxisHeaderMenuConfig>
    timeRangesFeature?         : object|boolean|string|TimeRanges|Partial<TimeRangesConfig>
    treeFeature?               : object|boolean|string|Tree|Partial<TreeConfig>
    treeGroupFeature?          : object|boolean|string|TreeGroup|Partial<TreeGroupConfig>
    versionsFeature?           : object|boolean|string|Versions|Partial<VersionsConfig>

}

export class BryntumGantt extends React.Component<BryntumGanttProps> {

    static instanceClass = Gantt;

    static instanceName = 'Gantt';

    processWidgetContent = processWidgetContent;

    // Component is View class
    static isView = true;

    // Hidden div to host unused portals DOM
    portalsCache? : HTMLElement;

    // CSS class used for individual portal containers
    portalContainerClass = 'b-react-portal-container';

    state = {
        // Holds React portals
        portals : new Map(),

        // Needed to trigger refresh when portals change
        generation : 0
    };

    static featureNames = [
        'baselinesFeature',
        'cellCopyPasteFeature',
        'cellEditFeature',
        'cellMenuFeature',
        'cellTooltipFeature',
        'columnAutoWidthFeature',
        'columnDragToolbarFeature',
        'columnLinesFeature',
        'columnPickerFeature',
        'columnRenameFeature',
        'columnReorderFeature',
        'columnResizeFeature',
        'criticalPathsFeature',
        'dependenciesFeature',
        'dependencyEditFeature',
        'eventFilterFeature',
        'eventSegmentsFeature',
        'excelExporterFeature',
        'fillHandleFeature',
        'filterFeature',
        'filterBarFeature',
        'groupFeature',
        'groupSummaryFeature',
        'headerMenuFeature',
        'headerZoomFeature',
        'indicatorsFeature',
        'labelsFeature',
        'mergeCellsFeature',
        'mspExportFeature',
        'multipageFeature',
        'multipageverticalFeature',
        'nonWorkingTimeFeature',
        'panFeature',
        'parentAreaFeature',
        'pdfExportFeature',
        'percentBarFeature',
        'progressLineFeature',
        'projectLinesFeature',
        'quickFindFeature',
        'regionResizeFeature',
        'rollupsFeature',
        'rowCopyPasteFeature',
        'rowExpanderFeature',
        'rowReorderFeature',
        'scheduleMenuFeature',
        'scheduleTooltipFeature',
        'searchFeature',
        'singlepageFeature',
        'sortFeature',
        'stickyCellsFeature',
        'stripeFeature',
        'summaryFeature',
        'taskCopyPasteFeature',
        'taskDragFeature',
        'taskDragCreateFeature',
        'taskEditFeature',
        'taskMenuFeature',
        'taskNonWorkingTimeFeature',
        'taskResizeFeature',
        'taskSegmentDragFeature',
        'taskSegmentResizeFeature',
        'taskTooltipFeature',
        'timeAxisHeaderMenuFeature',
        'timeRangesFeature',
        'treeFeature',
        'treeGroupFeature',
        'versionsFeature'
    ];

    projectStores = true;

    dataStores = {
        'assignmentStore': 'assignments',
        'calendarManagerStore': 'calendars',
        'dependencyStore': 'dependencies',
        'eventStore': 'events',
        'resourceStore': 'resources',
        'taskStore': 'tasks',
        'timeRangeStore': 'timeRanges'
    };

    static configNames = [
        'adopt',
        'align',
        'allowCreate',
        'allowOver',
        'anchor',
        'anchorToTarget',
        'animateRemovingRows',
        'ariaDescription',
        'ariaLabel',
        'autoAdjustTimeAxis',
        'autoClose',
        'autoHeight',
        'autoShow',
        'autoUpdateRecord',
        'bbar',
        'bodyCls',
        'bubbleEvents',
        'bufferCoef',
        'bufferThreshold',
        'callOnFunctions',
        'centered',
        'closable',
        'closeAction',
        'collapsed',
        'collapsible',
        'columns',
        'config',
        'constrainTo',
        'contentElementCls',
        'contextMenuTriggerEvent',
        'creationTooltip',
        'cycleResolutionPopupClass',
        'defaultBindProperty',
        'defaultFocus',
        'defaultRegion',
        'defaultResourceImageName',
        'defaults',
        'dependencyIdField',
        'destroyStore',
        'disableGridRowModelWarning',
        'dismissDelay',
        'displaySchedulingIssueResolutionPopup',
        'dock',
        'draggable',
        'durationDisplayPrecision',
        'enableDeleteKey',
        'enableRecurringEvents',
        'enableSticky',
        'enableTextSelection',
        'eventStyle',
        'features',
        'fillLastColumn',
        'fixedRowHeight',
        'floating',
        'focusOnToFront',
        'footer',
        'forElement',
        'forSelector',
        'fullRowRefresh',
        'getDateConstraints',
        'getHtml',
        'getRowHeight',
        'header',
        'hideAnimation',
        'hideDelay',
        'hideHeaders',
        'hideOnDelegateChange',
        'hideWhenEmpty',
        'hoverDelay',
        'htmlCls',
        'ignoreParentReadOnly',
        'infiniteScroll',
        'itemCls',
        'keyMap',
        'lazyItems',
        'listeners',
        'loadingMsg',
        'loadMask',
        'loadMaskDefaults',
        'loadMaskError',
        'localeClass',
        'localizable',
        'localizableProperties',
        'managedEventSizing',
        'maskDefaults',
        'masked',
        'maximizable',
        'maximized',
        'maxTimeAxisUnit',
        'modal',
        'monitorResize',
        'mouseOffsetX',
        'mouseOffsetY',
        'namedItems',
        'newTaskDefaults',
        'owner',
        'partner',
        'plugins',
        'positioned',
        'preserveFocusOnDatasetChange',
        'preserveScrollOnDatasetChange',
        'preventTooltipOnTouch',
        'projectProgressReporting',
        'relayStoreEvents',
        'resizeToFitIncludesHeader',
        'resourceImageFolderPath',
        'responsiveLevels',
        'ripple',
        'rootElement',
        'schedulingIssueResolutionPopupClass',
        'scrollAction',
        'scrollerClass',
        'scrollManager',
        'scrollTaskIntoViewOnCellClick',
        'selectionMode',
        'showAnimation',
        'showCreationTooltip',
        'showDirty',
        'showOnClick',
        'showOnHover',
        'showTooltip',
        'showTooltipWhenDisabled',
        'snapRelativeToEventStartDate',
        'stateful',
        'statefulEvents',
        'stateId',
        'stateProvider',
        'stickyHeaders',
        'strips',
        'subGridConfigs',
        'suppressFit',
        'syncMask',
        'tab',
        'tag',
        'taskRenderer',
        'tbar',
        'terminalCls',
        'terminalSides',
        'textAlign',
        'textContent',
        'timeAxis',
        'timeAxisColumn',
        'trackMouse',
        'trapFocus',
        'ui',
        'visibleZoomFactor',
        'weekStartDay',
        'weight',
        'zoomKeepsOriginalTimespan',
        'zoomOnMouseWheel',
        'zoomOnTimeAxisDoubleClick'
    ];

    static propertyConfigNames = [
        'alignSelf',
        'allowDropOnEventBar',
        'appendTo',
        'assignments',
        'barMargin',
        'calendars',
        'cls',
        'columnLines',
        'content',
        'creationTooltipTemplate',
        'data',
        'dataset',
        'dependencies',
        'disabled',
        'displayDateFormat',
        'emptyText',
        'enableEventAnimations',
        'enableUndoRedoKeys',
        'endDate',
        'eventColor',
        'extraData',
        'fillTicks',
        'flex',
        'forceFit',
        'height',
        'hidden',
        'html',
        'id',
        'insertBefore',
        'insertFirst',
        'items',
        'layout',
        'layoutStyle',
        'longPressTime',
        'margin',
        'maxHeight',
        'maxWidth',
        'maxZoomLevel',
        'minHeight',
        'minWidth',
        'minZoomLevel',
        'onAfterDependencySave',
        'onAfterDragCreate',
        'onAfterEventSave',
        'onAfterTaskDrop',
        'onAfterTaskEdit',
        'onAfterTaskSave',
        'onAfterTaskSegmentDrop',
        'onBeforeAssignmentDelete',
        'onBeforeCancelCellEdit',
        'onBeforeCellDelete',
        'onBeforeCellEditStart',
        'onBeforeClose',
        'onBeforeColumnDragStart',
        'onBeforeColumnDropFinalize',
        'onBeforeCopy',
        'onBeforeDependencyAdd',
        'onBeforeDependencyDelete',
        'onBeforeDependencyEdit',
        'onBeforeDependencyEditShow',
        'onBeforeDependencySave',
        'onBeforeDestroy',
        'onBeforeDragCreate',
        'onBeforeDragCreateFinalize',
        'onBeforeEventDelete',
        'onBeforeEventResize',
        'onBeforeEventResizeFinalize',
        'onBeforeEventSave',
        'onBeforeEventSegmentResize',
        'onBeforeEventSegmentResizeFinalize',
        'onBeforeFinishCellEdit',
        'onBeforeHide',
        'onBeforeMspExport',
        'onBeforePan',
        'onBeforePaste',
        'onBeforePdfExport',
        'onBeforePresetChange',
        'onBeforeRenderRow',
        'onBeforeRenderRows',
        'onBeforeSelectionChange',
        'onBeforeSetRecord',
        'onBeforeShow',
        'onBeforeTaskAdd',
        'onBeforeTaskDelete',
        'onBeforeTaskDrag',
        'onBeforeTaskDropFinalize',
        'onBeforeTaskEdit',
        'onBeforeTaskEditShow',
        'onBeforeTaskResize',
        'onBeforeTaskResizeFinalize',
        'onBeforeTaskSave',
        'onBeforeTaskSegmentDrag',
        'onBeforeTaskSegmentDropFinalize',
        'onBeforeTaskSegmentResize',
        'onBeforeTaskSegmentResizeFinalize',
        'onBeforeToggleGroup',
        'onBeforeToggleNode',
        'onCancelCellEdit',
        'onCatchAll',
        'onCellClick',
        'onCellContextMenu',
        'onCellDblClick',
        'onCellMenuBeforeShow',
        'onCellMenuItem',
        'onCellMenuShow',
        'onCellMenuToggleItem',
        'onCellMouseOut',
        'onCellMouseOver',
        'onCollapseNode',
        'onColumnDragStart',
        'onColumnDrop',
        'onContextMenuItem',
        'onContextMenuToggleItem',
        'onCopy',
        'onDataChange',
        'onDateRangeChange',
        'onDependenciesDrawn',
        'onDependencyClick',
        'onDependencyDblClick',
        'onDependencyMouseOut',
        'onDependencyMouseOver',
        'onDestroy',
        'onDragCreateEnd',
        'onDragCreateStart',
        'onDragSelecting',
        'onEventMenuBeforeShow',
        'onEventMenuItem',
        'onEventMenuShow',
        'onEventPartialResize',
        'onEventResizeEnd',
        'onEventResizeStart',
        'onEventSegmentPartialResize',
        'onEventSegmentResizeEnd',
        'onEventSegmentResizeStart',
        'onExpandNode',
        'onFinishCellEdit',
        'onFocusIn',
        'onFocusOut',
        'onGridRowAbort',
        'onGridRowBeforeDragStart',
        'onGridRowBeforeDropFinalize',
        'onGridRowDrag',
        'onGridRowDragStart',
        'onGridRowDrop',
        'onHeaderMenuBeforeShow',
        'onHeaderMenuItem',
        'onHeaderMenuShow',
        'onHeaderMenuToggleItem',
        'onHide',
        'onMouseOut',
        'onMouseOver',
        'onMspExport',
        'onNavigate',
        'onPaint',
        'onPaste',
        'onPdfExport',
        'onPercentBarDrag',
        'onPercentBarDragAbort',
        'onPercentBarDragStart',
        'onPercentBarDrop',
        'onPointerOver',
        'onPresetChange',
        'onReadOnly',
        'onRecompose',
        'onReleaseTask',
        'onRenderRow',
        'onRenderRows',
        'onRenderTask',
        'onResize',
        'onResponsive',
        'onScheduleMenuBeforeShow',
        'onScheduleMenuItem',
        'onScheduleMenuShow',
        'onScroll',
        'onSelectionChange',
        'onSelectionModeChange',
        'onShow',
        'onStartCellEdit',
        'onSubGridCollapse',
        'onSubGridExpand',
        'onTaskClick',
        'onTaskContextMenu',
        'onTaskDblClick',
        'onTaskDrag',
        'onTaskDragStart',
        'onTaskDrop',
        'onTaskEditCanceled',
        'onTaskKeyDown',
        'onTaskKeyUp',
        'onTaskMenuBeforeShow',
        'onTaskMenuItem',
        'onTaskMenuShow',
        'onTaskMouseDown',
        'onTaskMouseOut',
        'onTaskMouseOver',
        'onTaskMouseUp',
        'onTaskNonWorkingTimeClick',
        'onTaskNonWorkingTimeContextMenu',
        'onTaskNonWorkingTimeDblClick',
        'onTaskPartialResize',
        'onTaskResizeEnd',
        'onTaskResizeStart',
        'onTaskSegmentDrag',
        'onTaskSegmentDragStart',
        'onTaskSegmentDrop',
        'onTaskSegmentPartialResize',
        'onTaskSegmentResizeEnd',
        'onTaskSegmentResizeStart',
        'onTimeAxisChange',
        'onTimeAxisHeaderClick',
        'onTimeAxisHeaderContextMenu',
        'onTimeAxisHeaderDblClick',
        'onTimeAxisHeaderMenuBeforeShow',
        'onTimeAxisHeaderMenuItem',
        'onTimeAxisHeaderMenuShow',
        'onTimelineContextChange',
        'onTimelineViewportResize',
        'onTimeRangeHeaderClick',
        'onTimeRangeHeaderContextMenu',
        'onTimeRangeHeaderDblClick',
        'onToggleGroup',
        'onToggleNode',
        'onToolClick',
        'onTransactionChange',
        'onVisibleDateRangeChange',
        'presets',
        'project',
        'readOnly',
        'record',
        'resources',
        'rowHeight',
        'scrollable',
        'snap',
        'startDate',
        'store',
        'strictRecordMapping',
        'tasks',
        'taskStore',
        'tickSize',
        'timeRanges',
        'timeZone',
        'title',
        'toggleParentTasksOnClick',
        'tools',
        'tooltip',
        'tooltipTemplate',
        'transitionDuration',
        'updateTimelineContextOnScroll',
        'viewPreset',
        'visibleDate',
        'width',
        'workingTime',
        'x',
        'y'
    ];

    static propertyNames = [
        'anchorSize',
        'eventColors',
        'eventStyles',
        'isSettingValues',
        'isValid',
        'scrollLeft',
        'scrollTop',
        'scrollX',
        'selectedCell',
        'selectedCells',
        'selectedRecord',
        'selectedRecords',
        'selectedRows',
        'state',
        'timeResolution',
        'type',
        'values',
        'zoomLevel'
    ];

    // Component instance
    instance!: Gantt;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumGanttProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        const className = `b-react-${BryntumGantt.instanceClass.$name.toLowerCase()}-container`;
        return (
            <React.Fragment>
                <div className={className} ref={(element) => (this.element = element!)}>
                    {Array.from(this.state.portals).map((item) => item[1])}
                </div>
                <div ref={el => (this.portalsCache = el!)} className="b-react-portals-cache" style={{ display : 'none' }}></div>
            </React.Fragment>
        );

    }
}
